<script setup lang="ts">
import {ComponentPublicInstance, computed, reactive, ref, useTemplateRef, watch, nextTick, onMounted} from "vue";
import {useRoute, useRouter} from "vue-router";
import User from "@/Models/User";
import {Button, Icon} from "@/Components/Library";
import Comment from "./Comment.vue";
import type {Comment as CommentType} from "@/Types";
import {getting} from "@/Composables/api";
import {storeComment} from "@/Services/comments";
import {useIntersectionObserver} from "@vueuse/core";
import {useMainStore} from "@/Stores/store";
import CommentForm from "./CommentForm.vue";
import axios from "axios";

type ModelTypeBase = Record<string, any> & {
    comments: Array<CommentType>;
    comments_count?: number;
};

type ModelType = ModelTypeBase &
    (
        | {
              id: string;
          }
        | {
              id: number;
          }
    );

type Props = {
    model: ModelType;
    modelType: string;
    ownerId?: string;
    onlyOwnerReplies?: boolean;
    hideTitle?: boolean;
    showForm?: boolean;
    visibilityToggle?: boolean;
    disabled?: boolean;
    allowVoice?: boolean;
    hasFilters?: boolean;
    canAdmin?: boolean;
    highlightId?: string | number;
};

const $user = new User();
const $store = useMainStore();
const $route = useRoute();
const router = useRouter();

const {
    onlyOwnerReplies = false,
    showForm = true,
    visibilityToggle = false,
    allowVoice = false,
    canAdmin = false,
    highlightId = undefined,
    ...props
} = defineProps<Props>();
console.log("Comments:canAdmin", canAdmin);
const emit = defineEmits(["comment", "delete"]);
const params = reactive({
    page: -1,
    filter: {},
});
const isOwner = computed(() => $user.id === props.ownerId);
const commentsCount = computed(() => props.model.comments_count ?? props.model.comments?.length ?? 0);
const allowReply = computed(() => !onlyOwnerReplies || isOwner.value);
const comment = ref<{text: string; audioFile: Blob | null}>({text: "", audioFile: null});
const comments = computed(() => props.model.comments);
const commentTextarea = ref<ComponentPublicInstance | null>(null);
const commentForm = useTemplateRef<InstanceType<typeof CommentForm> | null>("commentForm");

const loadMore = ref(true);
const loadMoreIntersection = useTemplateRef("loadMoreIntersection");
const {} = useIntersectionObserver(
    loadMoreIntersection,
    ([entry]: any) => entry.isIntersecting && loadMore.value && params.page++,
);

const deleteReply = (comment: any, reply: any) => {
    comment.replies = comment.replies.filter((R: any) => R.id !== reply.id);
};

const activeHighlightId = ref<string | number | null>(null);

const scrollToAndHighlightComment = async (commentId: string | number) => {
    await nextTick();
    const element = document.getElementById(`comment-${commentId}`);
    if (element) {
        element.scrollIntoView({behavior: "smooth", block: "center"});
        activeHighlightId.value = commentId;
        setTimeout(() => {
            activeHighlightId.value = null;
        }, 3000);
    }
};

watch(
    () => [highlightId, comments.value],
    async ([newHighlightId]) => {
        if (newHighlightId && comments.value?.length) {
            await scrollToAndHighlightComment(newHighlightId);
        }
    },
    {immediate: true},
);

const postComment = async () => {
    let res;

    if (comment.value.audioFile) {
        let formData = {
            comment: "Voice message",
            group_id: router.currentRoute.value.params.group,
        };
        console.log("saving voice comment");
        const {data} = await storeComment(
            router.currentRoute.value.params.group,
            props.modelType,
            props.model.id,
            formData,
        );
        res = data;

        const audioFormData = new FormData();
        audioFormData.append("files[]", comment.value.audioFile, "audio.mp3");

        const {data: mediaData} = await axios.post(
            route("media.store", {
                modelType: "comment",
                modelId: res.id,
                collectionName: "audio",
            }),
            audioFormData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        );

        res.files = mediaData.data;
    } else {
        let formData = {
            comment: comment.value.text,
            group_id: router.currentRoute.value.params.group,
        };
        console.log("saving text comment", formData);
        const {data} = await storeComment(
            router.currentRoute.value.params.group,
            props.modelType,
            props.model.id,
            formData,
        );
        res = data;
    }

    comment.value = {text: "", audioFile: null};
    commentForm.value?.reset();
    emit("comment", res);

    if (props.modelType === "question") {
        if ($store.group?.id === $route.params.group) {
            let groupQuestion = $store.group?.questions?.find((Q) => Q.id === $route.params.question);

            if (groupQuestion) {
                groupQuestion.comments?.unshift(res);
                groupQuestion.comments_count++;
            }
        }
        let feedQuestion = $store.feed?.questions?.find((Q) => Q.id === $route.params.question);
        if (feedQuestion) {
            feedQuestion.comments?.unshift(res);
            feedQuestion.comments_count++;
        }
    }
};
defineExpose({
    commentTextarea,
    comment,
    postComment,
});
</script>
<template>
    <div class="w-full">
        <CommentForm
            ref="commentForm"
            v-if="showForm"
            v-model="comment"
            :class="[{'mb-6': comments.length}]"
            :allowVoice="allowVoice"
            @submit="postComment"
        />
        <div v-if="!comments?.length" class="w-full p-8">
            <p class="text-center text-stone-500">
                {{ hasFilters ? "No comments found for these filters." : "No comments yet." }}
            </p>
        </div>
        <div v-else v-auto-animate>
            <template v-for="(comment, i) in comments" :key="comment?.id">
                <div
                    :class="[
                        {
                            'border-b dark:border-b-stone-800': i + 1 !== comments.length,
                            'mb-3': i + 1 < comments.length,
                            'pb-3': i + 1 < comments.length || commentsCount > 2,
                            'bg-askr-green-500/10 transition-all duration-300': activeHighlightId === comment.id,
                        },
                    ]"
                >
                    <Comment
                        :comment="comment"
                        :allow-reply="allowReply"
                        :allow-voice="allowVoice"
                        :parent="{type: modelType, id: model.id}"
                        :disabled="disabled"
                        @delete="emit('delete', $event)"
                        :can-admin="canAdmin"
                    />
                    <!-- Comment replies -->
                    <div class="mt-6 pl-6 sm:pl-12" v-if="comment.replies?.length">
                        <template v-for="reply in comment.replies" :key="reply.id">
                            <Comment
                                class="mt-4"
                                :class="{
                                    'bg-askr-green-500/10 transition-all duration-300': activeHighlightId === reply.id,
                                }"
                                :comment="reply"
                                :allow-reply="false"
                                :parent="{type: 'comment', id: comment.id}"
                                :disabled="disabled"
                                @delete="deleteReply(comment, reply)"
                                :can-admin="canAdmin"
                            />
                        </template>
                    </div>
                </div>
            </template>
        </div>
        <div class="flex items-center justify-center">
            <Button
                v-if="comments.length < commentsCount"
                ref="loadMoreIntersection"
                class="border px-3 py-0 leading-none hover:bg-askr-green-50"
                @click="params.page++"
                :loading="!!getting"
                :disabled="!!getting || !loadMore"
            >
                <Icon icon="tabler:dots" width="20" />
            </Button>
            <!--            <Button-->
            <!--                v-else-if="commentsCount > 10"-->
            <!--                class="border px-3 py-0 leading-none hover:bg-askr-green-50"-->
            <!--                @click="hideComments"-->
            <!--            >-->
            <!--                <Icon icon="mingcute:up-fill" width="20" />-->
            <!--            </Button>-->
        </div>
    </div>
</template>
<style>
.comment-body a {
    @apply text-askr-green-500 underline transition-colors hover:text-black;
}
</style>
